import React, { useState } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { useCart } from "../../../hooks/useCart"
import { useCore } from "../../../hooks/useCore"
import { useApp } from "../../../hooks/useApp"
import { useWishlist } from "../../../hooks/useWishlist"

export const withAddToCart = Component => ({ name = "AddToCart", selectedVariant, product, layout, hideGender, gender, sizeConversions }) => {
  const {
    helpers: { decodeShopifyId, storage },
  } = useCore()

  const {
    config: {
      settings: {
        routes: { PRODUCT },
        keys: { preOrderKey, preOrderValue },
      },
    },
  } = useApp()

  const { addToCart, loading } = useCart()

  const { deleteFromWishlist } = useWishlist()

  const { productTemplateData, wishlistCard } = useStaticQuery(graphql`
    query SANITY_TEMPLATE_PRODUCT_ADD_TO_CART_AND_SANITY_PAGE_ACCOUNT_WISHLIST_CARD {
      productTemplateData: sanityTemplateProduct {
        selectSizeButtonText
        addToCartButtonText
        lowStockThreshold
        lowStockMessage
        preOrderButtonText
        backInStockButtonText
        backInStockMessage
        soldOutButtonText
        soldOutMessage
      }
      wishlistCard: sanityPageAccountWishlist {
        moveToCartButtonText
      }
    }
  `)

  const {
    selectSizeButtonText,
    addToCartButtonText,
    lowStockThreshold,
    lowStockMessage,
    preOrderButtonText,
    backInStockButtonText,
    backInStockMessage,
    soldOutButtonText,
    soldOutMessage,
  } = productTemplateData || {}

  const { moveToCartButtonText } = wishlistCard || {}

  const [restockFormActive, setRestockFormActive] = useState(false)

  const region = React.useRef(storage.get("size-region") || "AU")
  const hasSize = product?.tags?.find(tag => tag.includes("size:") && !tag.includes("size:ONE") && !tag.includes("size:EACH")) ? true : false

  //PreOrder data which Reactify Pre-Order App injected into metafield
  const preOrder = product?.metafields?.find(({ key, namespace }) => namespace === "reactify" && key === "preorder")?.value
    ? JSON.parse(product?.metafields?.find(({ key, namespace }) => namespace === "reactify" && key === "preorder")?.value)
    : ""

  // Display Restock form or not when variant is out of stock, supported by Shopify App - "alert me! RESTOCK alerts"
  const displayRestock = () => {
    if (!product?.metafields?.length) {
      return false
    }

    return !(product?.metafields?.find(({ key, namespace }) => namespace === "da-restock" && key === "hide")?.value === "false")
  }

  const isPreOrder = selectedVariant && selectedVariant?.quantityAvailable === 0 && selectedVariant?.availableForSale && !!preOrder

  const isRestock = selectedVariant && !selectedVariant?.availableForSale && displayRestock()

  const isOutOfStock = selectedVariant && !selectedVariant?.availableForSale && !displayRestock()

  const url = `${PRODUCT}/${product?.handle}`

  const addButtonText = layout === "wishlist" ? moveToCartButtonText : addToCartButtonText

  const getText = () => {
    if (selectedVariant?.quantityAvailable > 0 && selectedVariant?.quantityAvailable < lowStockThreshold)
      return { message: lowStockMessage.replace("${amount}", selectedVariant?.quantityAvailable), buttonText: addButtonText }
    if (isPreOrder)
      return {
        message: preOrder?.[decodeShopifyId(selectedVariant?.id, "ProductVariant")]?.message ?? preOrder?.all?.message,
        buttonText: preOrderButtonText,
      }
    if (isRestock) return { message: backInStockMessage, buttonText: backInStockButtonText }
    if (isOutOfStock) return { message: soldOutMessage, buttonText: soldOutButtonText }
    if (!selectedVariant) return { buttonText: selectSizeButtonText }
    return { buttonText: addButtonText }
  }

  const { message, buttonText } = getText()

  const getSize = size => {
    let sizeValue = size
    const sizeValueAU = size

    if (gender && sizeConversions && !hideGender && hasSize) {
      // Handle Unisex size conversion
      if (gender === "Unisex") {
        let womensSize = sizeConversions.find(sc => sc.mensSize == size)?.womensSize
        const womensSizeAU = sizeConversions.find(sc => sc.mensSize == size)?.womensSize

        // Size conversion for US and UK regions
        if (region.current === "US") {
          womensSize = sizeConversions.find(sc => sc.mensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "US")?.womensSize
          sizeValue = sizeConversions.find(sc => sc.mensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "US")?.mensSize
        }
        if (region.current === "UK") {
          womensSize = sizeConversions.find(sc => sc.mensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "UK")?.womensSize
          sizeValue = sizeConversions.find(sc => sc.mensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "UK")?.mensSize
        }

        return womensSize
          ? `Mens ${sizeValueAU} | Womens ${womensSizeAU} (Mens ${sizeValue} | Womens ${womensSize})`
          : `Mens ${sizeValueAU} (Mens ${sizeValue})`
      }
      // Handle Mens size conversion
      if (gender === "Mens") {
        // Size conversion for US and UK regions
        if (region.current === "US") {
          sizeValue = sizeConversions.find(sc => sc.mensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "US")?.mensSize
        }
        if (region.current === "UK") {
          sizeValue = sizeConversions.find(sc => sc.mensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "UK")?.mensSize
        }
        return `Mens ${sizeValueAU} (Mens ${sizeValue})`
      }

      // Handle Womens size conversion
      if (gender === "Womens") {
        // Size conversion for US and UK regions
        if (region.current === "US") {
          sizeValue = sizeConversions.find(sc => sc.womensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "US")?.womensSize
        }
        if (region.current === "UK") {
          sizeValue = sizeConversions.find(sc => sc.womensSize == size)?.multiRegionConversions?.find(mrc => mrc.region == "UK")?.womensSize
        }
        return `Womens ${sizeValueAU} (Womens ${sizeValue})`
      }
      return `${sizeValueAU} ${gender} ${sizeValue}`
    }
    return sizeValue
  }

  const handleAddToCart = async () => {
    const tags = product.tags

    // update region from storage
    region.current = storage.get("size-region") || "AU"

    const messages = tags.filter(tag => tag.includes("cart:")).map(tag => tag.replace("cart:", ""))

    const lineItemAttributes = []

    if (isPreOrder) {
      lineItemAttributes.push({
        key: preOrderKey,
        value: preOrderValue,
      })
    }

    if (gender && sizeConversions && !hideGender) {
      const selectedVariantSizeValue = selectedVariant?.selectedOptions?.find(svSize => svSize.name === "Size")?.value
      const sizeValue = getSize(selectedVariantSizeValue)
      // const sizeValueChanged = sizeValue.replace("Mens", `Mens ${region.current}`).replace("Womens", `Womens ${region.current}`)

      // Spliting AU size with other regions
      const auSizeValue = sizeValue?.split("(")[0]?.replace("Mens", `Mens AU`)?.replace("Womens", `Womens AU`)
      const restSizeValue = sizeValue?.split("(")[1]?.replace("Mens", `Mens ${region.current}`)?.replace("Womens", `Womens ${region.current}`)

      let normlizedSizeValue = `${auSizeValue} ${restSizeValue ? `(${restSizeValue}` : ""}`

      if (region.current === "AU") {
        normlizedSizeValue = normlizedSizeValue.split("(")[0]
      }

      lineItemAttributes.push({ key: "Size", value: normlizedSizeValue })
    } else {
      // TODO: Add support for wishlist item gender's size conversion
      const selectedVariantSizeValue = selectedVariant?.selectedOptions?.find(svSize => svSize.name === "Size")?.value
      lineItemAttributes.push({ key: "Size", value: selectedVariantSizeValue })
    }

    lineItemAttributes.push(...messages.map(message => ({ key: "Message", value: message })))

    await addToCart(product, selectedVariant, 1, lineItemAttributes)

    if (layout === "wishlist") {
      deleteFromWishlist(product?.id)
    }
  }

  const decodedVariantShopifyId = React.useMemo(() => {
    try {
      return decodeShopifyId(selectedVariant?.id, "ProductVariant")
    } catch (e) {
      return null
    }
  }, [selectedVariant?.id])

  const decodedProductShopifyId = React.useMemo(() => {
    try {
      return decodeShopifyId(product?.id, "Product")
    } catch (e) {
      return null
    }
  }, [product?.id])

  Component.displayName = name
  return (
    <Component
      handleAddToCart={handleAddToCart}
      loading={loading}
      selectedVariant={selectedVariant}
      message={message}
      buttonText={buttonText}
      isPreOrder={isPreOrder}
      isRestock={isRestock}
      isOutOfStock={isOutOfStock}
      restockFormActive={restockFormActive}
      setRestockFormActive={setRestockFormActive}
      variantId={decodedVariantShopifyId}
      productId={decodedProductShopifyId}
      url={url}
      layout={layout}
      hideGender={hideGender}
      gender={gender}
    />
  )
}
